import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { UpgradePrompt } from '../components/FeatureGate';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Upload, 
  Download, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Loader2,
  Factory,
  Truck,
  Users,
  Building2,
  MapPin,
  ArrowRight,
  RefreshCw,
  ExternalLink,
  Info,
  History,
  FileText,
  Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DATA_TYPES = [
  {
    id: 'suppliers',
    name: 'Suppliers',
    description: 'Import your subcontractors and equipment providers',
    icon: Factory,
    order: 1
  },
  {
    id: 'equipment',
    name: 'Assets',
    description: 'Import your fleet (requires suppliers first)',
    icon: Truck,
    order: 2,
    dependsOn: 'suppliers'
  },
  {
    id: 'operators',
    name: 'Operators',
    description: 'Import your operators (requires suppliers first)',
    icon: Users,
    order: 3,
    dependsOn: 'suppliers'
  },
  {
    id: 'customers',
    name: 'Customers',
    description: 'Import from your accounting package for invoicing',
    icon: Building2,
    order: 4,
    isSpecial: true
  },
  {
    id: 'sites',
    name: 'Sites',
    description: 'Import job sites (requires customers first)',
    icon: MapPin,
    order: 5,
    dependsOn: 'customers'
  }
];

export default function DataImport() {
  const { user, token } = useAuth();
  const { hasFeature, loading: tierLoading } = useTier();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(null);
  const [validationResults, setValidationResults] = useState(null);
  const [validating, setValidating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('import');

  // Check tier access
  const hasAccess = hasFeature('data_import');

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/import/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch import status:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/import/history?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setImportHistory(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch import history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, fetchHistory]);

  const downloadTemplate = async (dataType) => {
    try {
      const response = await fetch(`${API_URL}/api/import/template/${dataType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${dataType}_import_template.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`${dataType} template downloaded`);
      } else {
        toast.error('Failed to download template');
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileSelect = async (dataType, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setSelectedType(dataType);
    setSelectedFile(file);
    setValidating(true);
    setValidationResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_URL}/api/import/validate/${dataType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const results = await response.json();
        setValidationResults(results);
        setShowValidationDialog(true);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Validation failed');
        setSelectedFile(null);
      }
    } catch (error) {
      toast.error('Failed to validate file');
      setSelectedFile(null);
    } finally {
      setValidating(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const executeImport = async () => {
    if (!selectedType || !validationResults || !selectedFile) return;

    setImporting(true);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await fetch(`${API_URL}/api/import/execute/${selectedType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(`Successfully imported ${result.imported_count} new and updated ${result.updated_count} existing records`);
        } else {
          toast.warning(`Import completed with ${result.error_count} errors. ${result.imported_count} new, ${result.updated_count} updated.`);
        }
        setShowValidationDialog(false);
        setValidationResults(null);
        setSelectedFile(null);
        fetchStatus();
        fetchHistory(); // Refresh history after import
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Import failed');
      }
    } catch (error) {
      toast.error('Failed to execute import');
    } finally {
      setImporting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDataTypeIcon = (dataType) => {
    const icons = {
      suppliers: Factory,
      equipment: Truck,
      operators: Users,
      customers: Building2,
      sites: MapPin
    };
    return icons[dataType] || FileText;
  };

  const getStatusBadge = (dataType) => {
    if (!status) return null;
    const typeStatus = status[dataType];
    if (!typeStatus) return null;

    if (typeStatus.count > 0) {
      return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100">{typeStatus.message}</Badge>;
    } else if (!typeStatus.ready) {
      return <Badge variant="outline" className="text-amber-600 border-amber-300">{typeStatus.message}</Badge>;
    }
    return <Badge variant="outline">{typeStatus.message}</Badge>;
  };

  const isReady = (dataType) => {
    if (!status) return false;
    const typeStatus = status[dataType];
    return typeStatus?.ready !== false;
  };

  if (loading || tierLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Show upgrade prompt if no access
  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="data-import-page">
        <div>
          <h1 className="text-2xl font-semibold">Data Import</h1>
          <p className="text-muted-foreground">Import your existing data into EQUIPR CORE</p>
        </div>
        
        <UpgradePrompt 
          feature="data_import"
          title="Upgrade to Access Data Import"
          description="Bulk import suppliers, equipment, operators, customers, and sites. Save hours of manual data entry with our powerful import tool."
        />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-import-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Data Import</h1>
          <p className="text-muted-foreground">Import your existing data into EQUIPR CORE</p>
        </div>
        <Button variant="outline" onClick={() => { fetchStatus(); if (activeTab === 'history') fetchHistory(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="import" className="gap-2">
            <Upload className="h-4 w-4" />
            Import Data
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            Import History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="import" className="space-y-6 mt-6">
          {/* Import Order Info */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Recommended Import Order</AlertTitle>
            <AlertDescription className="flex items-center gap-2 mt-2">
              <span className="font-medium">Suppliers</span>
              <ArrowRight className="h-4 w-4" />
              <span className="font-medium">Assets</span>
              <ArrowRight className="h-4 w-4" />
              <span className="font-medium">Operators</span>
              <ArrowRight className="h-4 w-4" />
              <span className="font-medium">Customers</span>
              <ArrowRight className="h-4 w-4" />
              <span className="font-medium">Sites</span>
            </AlertDescription>
          </Alert>

          {/* Data Type Cards */}
          <div className="grid gap-4">
            {DATA_TYPES.map((type) => {
              const Icon = type.icon;
              const ready = isReady(type.id);
              const statusBadge = getStatusBadge(type.id);

              return (
                <Card key={type.id} className={!ready ? 'opacity-60' : ''} data-testid={`import-card-${type.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${ready ? 'bg-primary/10' : 'bg-muted'}`}>
                          <Icon className={`h-5 w-5 ${ready ? 'text-primary' : 'text-muted-foreground'}`} />
                        </div>
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            {type.name}
                            {statusBadge}
                          </CardTitle>
                          <CardDescription>{type.description}</CardDescription>
                        </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {type.isSpecial ? (
                      // Special handling for Customers
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCustomerDialog(true)}
                          data-testid={`import-btn-${type.id}`}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Connect Xero/MYOB
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadTemplate(type.id)}
                          data-testid={`download-template-${type.id}`}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          CSV Template
                        </Button>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => handleFileSelect(type.id, e)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            disabled={!ready || validating}
                            data-testid={`file-input-${type.id}`}
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!ready || validating}
                          >
                            {validating && selectedType === type.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4 mr-2" />
                            )}
                            Manual Import
                          </Button>
                        </div>
                      </>
                    ) : (
                      // Standard import flow
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadTemplate(type.id)}
                          disabled={!ready}
                          data-testid={`download-template-${type.id}`}
                        >
                          <Download className="h-4 w-4 mr-2" />
                          Download Template
                        </Button>
                        <div className="relative">
                          <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => handleFileSelect(type.id, e)}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            disabled={!ready || validating}
                            data-testid={`file-input-${type.id}`}
                          />
                          <Button
                            disabled={!ready || validating}
                            data-testid={`import-btn-${type.id}`}
                          >
                            {validating && selectedType === type.id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Upload className="h-4 w-4 mr-2" />
                            )}
                            Import CSV
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
            </Card>
          );
        })}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          {historyLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : importHistory.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <History className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium">No Import History</h3>
                <p className="text-muted-foreground mt-1">
                  Your import history will appear here after you complete your first import.
                </p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Import History</CardTitle>
                <CardDescription>
                  View all past data imports performed by administrators
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {importHistory.map((log) => {
                    const Icon = getDataTypeIcon(log.data_type);
                    const hasErrors = log.error_count > 0;
                    
                    return (
                      <div 
                        key={log.id} 
                        className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                        data-testid={`history-entry-${log.id}`}
                      >
                        <div className={`p-2 rounded-lg ${hasErrors ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-green-100 dark:bg-green-900/30'}`}>
                          <Icon className={`h-5 w-5 ${hasErrors ? 'text-amber-600' : 'text-green-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium capitalize">{log.data_type}</span>
                            <Badge variant={hasErrors ? 'outline' : 'secondary'} className={hasErrors ? 'border-amber-300 text-amber-700' : ''}>
                              {hasErrors ? 'Completed with errors' : 'Success'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              {formatDate(log.created_at)}
                            </span>
                            <span>by {log.imported_by_name}</span>
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-green-600">
                              <CheckCircle2 className="h-3.5 w-3.5 inline mr-1" />
                              {log.imported_count} new
                            </span>
                            <span className="text-blue-600">
                              <RefreshCw className="h-3.5 w-3.5 inline mr-1" />
                              {log.updated_count} updated
                            </span>
                            {log.error_count > 0 && (
                              <span className="text-red-600">
                                <XCircle className="h-3.5 w-3.5 inline mr-1" />
                                {log.error_count} errors
                              </span>
                            )}
                            <span className="text-muted-foreground">
                              {log.total_rows} total rows
                            </span>
                          </div>
                          {log.filename && (
                            <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
                              <FileText className="h-3 w-3" />
                              {log.filename}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Validation Results Dialog */}
      <Dialog open={showValidationDialog} onOpenChange={setShowValidationDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Import Preview - {selectedType}</DialogTitle>
            <DialogDescription>
              Review the validation results before importing
            </DialogDescription>
          </DialogHeader>
          
          {validationResults && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <div className="text-2xl font-bold text-green-600">{validationResults.new_count}</div>
                  <div className="text-sm text-muted-foreground">New</div>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <div className="text-2xl font-bold text-blue-600">{validationResults.update_count}</div>
                  <div className="text-sm text-muted-foreground">Updates</div>
                </div>
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
                  <div className="text-2xl font-bold text-red-600">{validationResults.error_count}</div>
                  <div className="text-sm text-muted-foreground">Errors</div>
                </div>
                <div className="p-3 bg-muted rounded-lg text-center">
                  <div className="text-2xl font-bold">{validationResults.rows?.length || 0}</div>
                  <div className="text-sm text-muted-foreground">Total Rows</div>
                </div>
              </div>

              {/* Row Details */}
              <ScrollArea className="h-[300px] border rounded-lg">
                <div className="p-4 space-y-2">
                  {validationResults.rows?.map((row) => (
                    <div
                      key={row.row}
                      className={`p-3 rounded-lg border ${
                        row.status === 'error' 
                          ? 'bg-red-50 dark:bg-red-900/20 border-red-200' 
                          : row.status === 'update'
                          ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200'
                          : 'bg-green-50 dark:bg-green-900/20 border-green-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5">
                          {row.status === 'error' ? (
                            <XCircle className="h-5 w-5 text-red-500" />
                          ) : row.status === 'update' ? (
                            <AlertCircle className="h-5 w-5 text-blue-500" />
                          ) : (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Row {row.row}</span>
                            <Badge variant={row.status === 'error' ? 'destructive' : row.status === 'update' ? 'secondary' : 'default'}>
                              {row.status === 'new' ? 'New' : row.status === 'update' ? 'Will Update' : 'Error'}
                            </Badge>
                          </div>
                          {row.data && (
                            <div className="text-sm text-muted-foreground mt-1">
                              {Object.entries(row.data).filter(([k, v]) => v && !k.includes('_id')).slice(0, 3).map(([key, value]) => (
                                <span key={key} className="mr-3">
                                  <span className="font-medium">{key}:</span> {String(value).substring(0, 30)}
                                </span>
                              ))}
                            </div>
                          )}
                          {row.errors && (
                            <div className="text-sm text-red-600 mt-1">
                              {row.errors.map((err, i) => (
                                <div key={i}>{err}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowValidationDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={executeImport} 
              disabled={importing || !validationResults || validationResults.valid_count === 0}
            >
              {importing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Import {validationResults?.valid_count || 0} Records
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Import Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Customers</DialogTitle>
            <DialogDescription>
              Customers should be imported from your accounting package to ensure invoicing works correctly.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {status?.customers?.has_integration ? (
              <Alert>
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <AlertTitle>Connected to {status.customers.integration_type === 'xero' ? 'Xero' : 'MYOB'}</AlertTitle>
                <AlertDescription>
                  Go to Settings → Accounting to sync your customers.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertTitle>No accounting package connected</AlertTitle>
                  <AlertDescription>
                    Connect Xero or MYOB to automatically sync your customers.
                  </AlertDescription>
                </Alert>
                
                <div className="grid grid-cols-2 gap-4">
                  <Button 
                    variant="outline" 
                    className="h-20 flex-col"
                    onClick={() => {
                      setShowCustomerDialog(false);
                      window.location.href = '/settings?tab=accounting';
                    }}
                  >
                    <img src="https://www.xero.com/content/dam/xero/images/logo/xero-logo-hires-RGB.png" alt="Xero" className="h-6 mb-2" />
                    Connect Xero
                  </Button>
                  <Button 
                    variant="outline"
                    className="h-20 flex-col"
                    onClick={() => {
                      setShowCustomerDialog(false);
                      window.location.href = '/settings?tab=accounting';
                    }}
                  >
                    <span className="font-bold text-lg mb-2">MYOB</span>
                    Connect MYOB
                  </Button>
                </div>
              </>
            )}
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or import manually
                </span>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground text-center">
              Don't use accounting software? You can still import customers via CSV.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCustomerDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="secondary"
              onClick={() => {
                setShowCustomerDialog(false);
                downloadTemplate('customers');
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Download CSV Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
